import { IRowNode } from 'ag-grid-community';
import { RadioGroup } from 'cdk-radial';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ActiveAuthorizePaymentPage, CHECK, Tabs } from 'common/constants';
import { useAmplitude } from 'components/AmplitudeProvider';
import { ComposedTableDataType } from 'components/ComposedTable/types';
import printGridData from 'components/PrintViewer/PdfExport/printGridData';
import { setSuccessMessage } from 'components/SuccessMessage/slice';
import { Toast } from 'components/Toast';
import { useFlagValue } from 'hooks';
import { useSubmitArchiveData } from 'hooks/useClientDataServices';
import { getSelectedTab } from 'pages/LandingPage/components/LandingPageHeader/slice';
import { getActiveAuthorizePaymentPage } from 'pages/VendorPaymentResultPage/selectors';
import {
  getVendorsDataQueryOptions,
  getSelectedVendorInvoicePairs
} from 'pages/VendorsTablePage/components/VendorsTable/slice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { retrievePaymentMethod } from 'utils/RetrievePaymentMethod';
import { archiveData } from './archiveData';
import {
  ARCHIVE_FILE_TYPE,
  EXPORT_FILE_TYPE,
  IS_SELECTED_FIELD_NAME
} from './constants';
import {
  getFooterContent,
  getHeaderContent,
  isVendorSelected,
  isInvoiceSelected,
  exportArchiveAmplitude
} from './helpers';
import { labels, messages } from './messages';
import {
  StyledDownloadDialog,
  StyledRadioComponent,
  StyledToast
} from './styled';
import { ExportOrArchiveType, FileType } from './types';

export const ExportOrArchiveDialog = <
  TData extends ComposedTableDataType,
  TExportData extends ComposedTableDataType = Record<string, never>
>({
  exportData,
  getCSVDocument,
  getExcelDocument,
  getPDFDocument,
  isExportDataFetching,
  rowModelType,
  setIsExportDataQueryEnabled,
  isOpen,
  downloadDialogTitle,
  onClose,
  gridRef,
  totalsInFooter,
  tableHeaderFilters,
  columnDefs
}: ExportOrArchiveType<TData, TExportData>): React.JSX.Element => {
  const [isArchiveError, setIsArchiveError] = useState<boolean>(false);
  const [isFileGenerationError, setIsFileGenerationError] =
    useState<boolean>(false);
  const [isExportSelected, setIsExportSelected] = useState<boolean>(true);
  const [isArchiveLoading, setIsArchiveLoading] = useState<boolean>(false);
  const [fileType, setFileType] = useState<FileType>(FileType.XLS);
  const dispatch = useAppDispatch();
  const selectedTab = useAppSelector(getSelectedTab);
  const { paymentMethod } = useAppSelector(getVendorsDataQueryOptions);
  const activeAuthorizePaymentPage = useAppSelector(
    getActiveAuthorizePaymentPage
  );
  const selectedVendorInvoicePairs = useAppSelector(
    getSelectedVendorInvoicePairs
  );
  const landingPageFlag = useFlagValue('dmsAccountingLandingPage');

  const { mutate: postArchiveData } = useSubmitArchiveData(fileType);
  const intl = useIntl();
  const amplitude = useAmplitude();
  const interCompanyFlag = useFlagValue('dmsAccountingIntercompanyPayment');

  const getHeaderString = () =>
    getHeaderContent(
      selectedTab,
      intl,
      tableHeaderFilters,
      activeAuthorizePaymentPage,
      interCompanyFlag
    );

  const getFooterString = () => {
    if (activeAuthorizePaymentPage !== '') {
      return '';
    }
    return getFooterContent(selectedTab, intl, totalsInFooter);
  };

  const csvContent = (content: string) => [
    [],
    [
      {
        data: {
          value: content
        }
      }
    ]
  ];

  const isLoading = isExportDataFetching || isArchiveLoading;

  const handleExport = () => {
    if (rowModelType !== 'serverSide') {
      if (gridRef?.current?.api && columnDefs?.length) {
        const gridDataArray: TData[] = [];
        const updatedColDefs = [...columnDefs];
        if (
          selectedTab === Tabs.NeedsAuthorization &&
          activeAuthorizePaymentPage === ''
        )
          updatedColDefs.unshift({
            field: IS_SELECTED_FIELD_NAME as any,
            headerName: intl.formatMessage({
              defaultMessage: 'Selected',
              description: 'Header name for isSelected'
            }),
            hide: true,
            suppressColumnsToolPanel: true
          });
        if (
          activeAuthorizePaymentPage ===
          ActiveAuthorizePaymentPage.AuthorizeResult
        ) {
          const checkboxIndex = updatedColDefs.findIndex(
            ({ headerName }) => headerName === CHECK.toLowerCase()
          );
          if (checkboxIndex !== -1) updatedColDefs.splice(checkboxIndex, 1);
        }
        gridRef.current.api.setGridOption('columnDefs', updatedColDefs);
        gridRef.current.api.forEachNode(rowNode => {
          if (rowNode?.data) {
            /* eslint-disable-next-line */
            gridDataArray.push({
              ...rowNode.data,
              isSelectedForExport: isInvoiceSelected(
                selectedVendorInvoicePairs,
                rowNode.data
              )
            });
          }
        });
        gridRef.current.api.setGridOption('rowData', gridDataArray);
        gridRef.current.api.forEachNode((rowNode: IRowNode, _) => {
          if (rowNode?.aggData && rowNode?.key)
            /* eslint-disable-next-line */
            rowNode.aggData.isSelectedForExport = isVendorSelected(
              selectedVendorInvoicePairs,
              rowNode.key
            );
        });
        const exportCols = [
          'isSelectedForExport',
          ...gridRef.current.api
            .getAllDisplayedColumns()
            .map(col => col.getColDef().field)
        ];

        const headerContent = getHeaderString();
        const footerContent = getFooterString();

        if (fileType === FileType.XLS) {
          gridRef.current.api.exportDataAsExcel({
            columnKeys: exportCols as string[],
            prependContent: [
              { cells: [] },
              {
                cells: [
                  {
                    data: {
                      value: headerContent,
                      type: 'String'
                    }
                  }
                ]
              }
            ],
            appendContent: [
              { cells: [] },
              {
                cells: [
                  {
                    data: {
                      value: footerContent,
                      type: 'String'
                    }
                  }
                ]
              }
            ]
          });
        } else if (fileType === FileType.CSV) {
          gridRef.current.api.exportDataAsCsv({
            columnKeys: exportCols as string[],
            prependContent: csvContent(headerContent),
            appendContent: csvContent(footerContent)
          });
        } else {
          printGridData(
            gridRef,
            {
              selectedTab,
              intl,
              totalsInFooter,
              tableHeaderFilters,
              activeAuthorizePaymentPage
            },
            selectedVendorInvoicePairs,
            true,
            interCompanyFlag
          );
        }
        exportArchiveAmplitude(
          EXPORT_FILE_TYPE,
          fileType,
          selectedTab,
          amplitude
        );
      }
      onClose();
    } else {
      setIsExportDataQueryEnabled?.(true);
    }
  };

  const handleArchive = () => {
    if (rowModelType !== 'serverSide') {
      if (gridRef?.current?.api) {
        setIsArchiveLoading(true);
        const headerContent = getHeaderString();
        const footerContent = getFooterString();
        const exportCols = [
          'isSelectedForArchive',
          ...gridRef.current.api
            .getAllDisplayedColumns()
            .map(col => col.getColDef().field)
        ];
        const archivePaymentMethod = retrievePaymentMethod<TData>(
          gridRef,
          selectedTab,
          paymentMethod,
          landingPageFlag
        );
        const formData = archiveData({
          gridRef,
          fileType,
          headerContent,
          footerContent,
          exportCols
        });
        postArchiveData(
          { formData, archivePaymentMethod },
          {
            onSuccess: () => {
              onClose();
              dispatch(
                setSuccessMessage(
                  intl.formatMessage(messages.successMessageForArchive)
                )
              );
            },
            onError: () => {
              setIsArchiveError(true);
            },
            onSettled: () => {
              setIsArchiveLoading(false);
            }
          }
        );
        exportArchiveAmplitude(
          ARCHIVE_FILE_TYPE,
          fileType,
          selectedTab,
          amplitude
        );
      } else {
        onClose();
      }
    } else {
      setIsArchiveLoading(true);
      setIsExportDataQueryEnabled?.(true);
    }
  };

  const handleExportDataResponse = useCallback(async () => {
    if (exportData && !isExportDataFetching && isOpen) {
      let fileBlob: Blob | undefined;
      let fileExtension: string | undefined;
      try {
        if (fileType === FileType.PDF && getPDFDocument && getExcelDocument) {
          fileBlob = isExportSelected
            ? await getPDFDocument(exportData)
            : getExcelDocument(exportData);
          fileExtension = '.pdf';
        } else if (fileType === FileType.CSV && getCSVDocument) {
          const csvData = getCSVDocument(exportData);
          fileBlob = new Blob([csvData], { type: 'text/csv' });
          fileExtension = '.csv';
        } else if (fileType === FileType.XLS && getExcelDocument) {
          fileBlob = getExcelDocument(exportData);
          fileExtension = '.xls';
        }
      } catch (err) {
        console.error(`Error generating or downloading ${fileType}:`, err);
        setIsFileGenerationError(true);
      }
      if (fileBlob === undefined) {
        return;
      }
      if (isExportSelected) {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(fileBlob);
        link.download = `Export${fileExtension}`;
        link.click();
      } else {
        const formData = new FormData();
        formData.append(
          'dataFile',
          new File([fileBlob], `Archive${fileExtension}`)
        );
        if (gridRef) {
          const archivePaymentMethod = retrievePaymentMethod<TData>(
            gridRef,
            selectedTab,
            paymentMethod,
            landingPageFlag
          );
          postArchiveData(
            { formData, archivePaymentMethod },
            {
              onSuccess: () => {
                onClose();
                dispatch(
                  setSuccessMessage(
                    intl.formatMessage(messages.successMessageForArchive)
                  )
                );
              },
              onError: () => {
                setIsArchiveError(true);
              },
              onSettled: () => {
                setIsArchiveLoading(false);
              }
            }
          );
        }
      }
      exportArchiveAmplitude(
        ARCHIVE_FILE_TYPE,
        fileType,
        selectedTab,
        amplitude
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData, isExportDataFetching]);

  useEffect(() => {
    handleExportDataResponse();
  }, [handleExportDataResponse]);

  const buttonForDialog = [
    {
      id: 'export-or-archive',
      onClick: isExportSelected ? handleExport : handleArchive,
      text: intl.formatMessage(
        isExportSelected ? labels.actionExportLabel : labels.actionArchiveLabel
      ),
      isDisabled: isLoading,
      isLoading,
      variant: 'text'
    }
  ];

  const actionRadioOptions = [
    {
      id: 'export-action',
      label: intl.formatMessage(labels.actionExportLabel),
      value: 'export',
      checked: isExportSelected,
      onChange: () => setIsExportSelected(true)
    },
    {
      id: 'archive-action',
      label: intl.formatMessage(labels.actionArchiveLabel),
      value: 'archive',
      checked: !isExportSelected,
      onChange: () => setIsExportSelected(false)
    }
  ];

  const fileTypeRadioOptions = [
    {
      id: 'xls',
      label: intl.formatMessage(labels.xlsFileTypeLabel),
      value: 'xls',
      checked: fileType === FileType.XLS,
      onChange: () => setFileType(FileType.XLS)
    },
    {
      id: 'csv',
      label: intl.formatMessage(labels.csvFileTypeLabel),
      value: 'csv',
      checked: fileType === FileType.CSV,
      onChange: () => setFileType(FileType.CSV)
    },
    {
      id: 'pdf',
      label: intl.formatMessage(labels.pdfFileTypeLabel),
      value: 'pdf',
      checked: fileType === FileType.PDF,
      onChange: () => setFileType(FileType.PDF)
    }
  ];

  return (
    <StyledDownloadDialog
      buttonsProps={buttonForDialog}
      handleClose={() => {
        if (!isLoading) {
          onClose();
        }
      }}
      title={
        downloadDialogTitle || intl.formatMessage(labels.defaultDialogTitle)
      }
      open={isOpen}
      dataTestId="export-or-archive-dialog"
      id="export-or-archive-dialog"
    >
      {isArchiveError && (
        <StyledToast>
          <Toast
            buttonProps={[
              {
                text: intl.formatMessage(labels.dismiss),
                onClick: () => {
                  setIsArchiveError(false);
                }
              }
            ]}
            content={intl.formatMessage(messages.errorMessageForArchive)}
          />
        </StyledToast>
      )}
      {isFileGenerationError && (
        <StyledToast>
          <Toast
            buttonProps={[
              {
                text: intl.formatMessage(labels.dismiss),
                onClick: () => {
                  setIsFileGenerationError(false);
                }
              }
            ]}
            content={intl.formatMessage(
              messages.errorMessageForFileGeneration,
              { fileType }
            )}
          />
        </StyledToast>
      )}
      <StyledRadioComponent>
        {intl.formatMessage(labels.actionLabel)}
      </StyledRadioComponent>
      <RadioGroup
        dataTestId="action-type-radio-group"
        id="action-type-radio-group"
        options={actionRadioOptions}
      />
      <StyledRadioComponent>
        {intl.formatMessage(labels.fileTypeLabel)}
      </StyledRadioComponent>
      <RadioGroup
        dataTestId="file-type-radio-group"
        id="file-type-radio-group"
        name="file-type-radio-group"
        options={fileTypeRadioOptions}
      />
    </StyledDownloadDialog>
  );
};
